// src/components/ModalImportarTagsRFID.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
import { doc, collection, getDocs, writeBatch, deleteField } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import Swal from 'sweetalert2';
import {
  FaIdCard,
  FaTimes,
  FaDownload,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaExclamationCircle,
  FaFileExcel,
  FaInfoCircle,
  FaShieldAlt,
  FaSearch
} from 'react-icons/fa';
import {
  TAG_KEYS,
  getTagLabel,
  cleanTagValue,
  getTagEquivalents,
  auditFraccionamientoTags,
  normalizeColumnKey
} from '../utils/rfidTagUtils';
import './ModalImportarTagsRFID.css';

export default function ModalImportarTagsRFID({
  isOpen,
  onClose,
  fraccionamientoId: propFraccionamientoId,
  casas: propCasas,
  onSuccess
}) {
  const { userData, fraccionamientoId: authFraccionamientoId } = useAuth();
  const fraccionamientoId = propFraccionamientoId || authFraccionamientoId || userData?.fraccionamientoId || null;

  const [casasLocales, setCasasLocales] = useState([]);
  const [cargandoCasas, setCargandoCasas] = useState(false);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [procesandoArchivo, setProcesandoArchivo] = useState(false);
  const [aplicandoCambios, setAplicandoCambios] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [progresoCarga, setProgresoCarga] = useState(null);
  const [importacionCompletada, setImportacionCompletada] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [filtroTabla, setFiltroTabla] = useState('todos'); // 'todos' | 'cambios' | 'conflictos'
  const [busquedaTabla, setBusquedaTabla] = useState('');
  const [mostrarDuplicadosBd, setMostrarDuplicadosBd] = useState(false);

  const [resultadoAuditoria, setResultadoAuditoria] = useState(null);
  const fileInputRef = useRef(null);

  // Cargar casas si no fueron pasadas por props
  useEffect(() => {
    if (!isOpen || !fraccionamientoId) return;

    if (Array.isArray(propCasas) && propCasas.length > 0) {
      setCasasLocales(propCasas);
      return;
    }

    let isMounted = true;
    const fetchCasas = async () => {
      try {
        setCargandoCasas(true);
        const colRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const snap = await getDocs(colRef);
        if (!isMounted) return;
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCasasLocales(list);
      } catch (err) {
        console.error('[ModalImportarTagsRFID] Error cargando casas:', err);
      } finally {
        if (isMounted) setCargandoCasas(false);
      }
    };

    fetchCasas();
    return () => { isMounted = false; };
  }, [isOpen, fraccionamientoId, propCasas]);

  // Auditoría previa de la base de datos (detecta si ya hay duplicados como SF01 y SF28)
  const auditoriaBdActual = useMemo(() => {
    if (!casasLocales.length) return { tagToCasaMap: new Map(), duplicateConflicts: [] };
    return auditFraccionamientoTags(casasLocales);
  }, [casasLocales]);

  // Casas ordenadas numéricamente
  const casasOrdenadas = useMemo(() => {
    const list = [...casasLocales];
    list.sort((a, b) => {
      const numA = String(a.numero || a.numeroCasa || a.lote || '').trim();
      const numB = String(b.numero || b.numeroCasa || b.lote || '').trim();
      return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [casasLocales]);

  // Limpiar estados al cerrar
  const handleClose = () => {
    if (aplicandoCambios) return;
    setArchivoSeleccionado(null);
    setResultadoAuditoria(null);
    setMostrarConfirmacion(false);
    setProgresoCarga(null);
    setImportacionCompletada(null);
    setFiltroTabla('todos');
    setBusquedaTabla('');
    setMostrarDuplicadosBd(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  // 1. Descarga de plantilla oficial en Excel (.xlsx) con datos vigentes
  const handleDownloadPlantilla = () => {
    try {
      const nombreFracc = userData?.nombreFraccionamiento || 'Fraccionamiento';
      const fechaHoy = new Date().toISOString().split('T')[0];

      const filasExcel = casasOrdenadas.map((casa) => {
        const numCasa = String(casa.numero || casa.numeroCasa || casa.lote || casa.id).trim();
        const nombre = String(casa.nombre_residente || casa.nombre || casa.nombreCompleto || '').trim();

        const fila = {
          'ID_Sistema': casa.id,
          'Numero_Casa': numCasa,
          'Nombre_Residente': nombre,
        };

        TAG_KEYS.forEach((tk, idx) => {
          const val = casa[tk] ? String(casa[tk]).trim() : '';
          fila[`Tag_${idx + 1}`] = val;
        });

        return fila;
      });

      const worksheet = XLSX.utils.json_to_sheet(filasExcel);

      // Anchos de columna óptimos
      worksheet['!cols'] = [
        { wch: 22 }, // ID_Sistema
        { wch: 14 }, // Numero_Casa
        { wch: 28 }, // Nombre_Residente
        { wch: 14 }, // Tag_1
        { wch: 14 }, // Tag_2
        { wch: 14 }, // Tag_3
        { wch: 14 }, // Tag_4
        { wch: 14 }, // Tag_5
        { wch: 14 }, // Tag_6
        { wch: 14 }, // Tag_7
        { wch: 14 }, // Tag_8
        { wch: 14 }, // Tag_9
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tarjetas_RFID');

      const fileName = `Plantilla_RFID_Tags_${nombreFracc.replace(/[^a-zA-Z0-9]/g, '_')}_${fechaHoy}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('[ModalImportarTagsRFID] Error descargando plantilla:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error al generar la plantilla',
        text: 'Ocurrió un inconveniente al crear el archivo Excel. Inténtalo nuevamente.'
      });
    }
  };

  // 2. Procesamiento y auditoría exhaustiva del archivo subido
  const procesarArchivoExcel = async (file) => {
    if (!file) return;
    setProcesandoArchivo(true);
    setArchivoSeleccionado(file);
    setResultadoAuditoria(null);
    setMostrarConfirmacion(false);
    setProgresoCarga(null);
    setImportacionCompletada(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (!rows || rows.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Archivo sin datos',
          text: 'El archivo Excel seleccionado no contiene filas con información.'
        });
        setProcesandoArchivo(false);
        return;
      }

      // Mapas de indexación de casas actuales
      const casaByIdMap = new Map();
      const casaByNumMap = new Map();
      casasLocales.forEach((c) => {
        casaByIdMap.set(c.id, c);
        const num = String(c.numero || c.numeroCasa || c.lote || '').trim().toUpperCase();
        if (num) casaByNumMap.set(num, c);
      });

      // Mapeo inteligente de encabezados de cada fila
      const filasAuditadas = [];
      const globalTagsMap = new Map(); // tagNorm -> array de { casaId, numCasa, tagKey, filaIndex }
      const conflictos = [];

      rows.forEach((row, rowIndex) => {
        const numFilaExcel = rowIndex + 2;
        let idSistemaVal = '';
        let numeroCasaVal = '';
        let nombreResidenteVal = '';
        const tagsExtraidos = {};

        // Normalizar nombres de columnas
        Object.keys(row).forEach((colName) => {
          const normKey = normalizeColumnKey(colName);
          const rawVal = cleanTagValue(row[colName]);

          if (['idsistema', 'id', 'idcasa'].includes(normKey)) {
            idSistemaVal = rawVal;
          } else if (['numerocasa', 'numero', 'casa', 'lote'].includes(normKey)) {
            numeroCasaVal = rawVal;
          } else if (['nombreresidente', 'nombre', 'residente'].includes(normKey)) {
            nombreResidenteVal = rawVal;
          } else {
            // Revisar si corresponde a tag1 .. tag9
            for (let i = 1; i <= 9; i++) {
              if (normKey === `tag${i}` || normKey === `tag0${i}` || (normKey === 'tag' && i === 1)) {
                const key = TAG_KEYS[i - 1];
                tagsExtraidos[key] = rawVal;
              }
            }
          }
        });

        // Identificar la casa correspondiente en el fraccionamiento
        let casaCoincidente = null;
        if (idSistemaVal && casaByIdMap.has(idSistemaVal)) {
          casaCoincidente = casaByIdMap.get(idSistemaVal);
        } else if (numeroCasaVal) {
          const normNum = numeroCasaVal.toUpperCase();
          if (casaByNumMap.has(normNum)) {
            casaCoincidente = casaByNumMap.get(normNum);
          }
        }

        if (!casaCoincidente) {
          conflictos.push({
            tipo: 'CASA_NO_ENCONTRADA',
            mensaje: `Fila ${numFilaExcel}: No se encontró la vivienda "${numeroCasaVal || idSistemaVal || 'Sin datos'}" en el sistema.`,
            fila: numFilaExcel
          });
          filasAuditadas.push({
            filaExcel: numFilaExcel,
            casaId: idSistemaVal || `fila_${rowIndex}`,
            numeroCasa: numeroCasaVal || 'Desconocida',
            nombreResidente: nombreResidenteVal,
            tagsNuevos: tagsExtraidos,
            tagsActuales: {},
            tieneCambios: false,
            conflictosFila: [`No se localizó la vivienda en el sistema.`],
            estado: 'conflicto'
          });
          return;
        }

        const casaId = casaCoincidente.id;
        const numCasaFinal = String(casaCoincidente.numero || casaCoincidente.numeroCasa || numeroCasaVal || casaId).trim();
        const nombreFinal = nombreResidenteVal || casaCoincidente.nombre_residente || casaCoincidente.nombre || 'Residente';

        // Validar tags en la fila
        const conflictosFila = [];
        const tagsActuales = {};
        TAG_KEYS.forEach((tk) => {
          tagsActuales[tk] = cleanTagValue(casaCoincidente[tk]);
        });

        // 1. Detectar duplicados dentro de la misma vivienda (ej: Tag 1 = Tag 2)
        const tagsVistosEnFila = new Map();
        TAG_KEYS.forEach((tk) => {
          const val = tagsExtraidos[tk];
          if (val) {
            // Validar caracteres alfanuméricos
            if (!/^[a-zA-Z0-9]+$/.test(val)) {
              const msg = `${getTagLabel(tk)} ("${val}") contiene caracteres no permitidos (solo letras y números).`;
              conflictosFila.push(msg);
              conflictos.push({ tipo: 'CARACTERES_INVALIDOS', mensaje: `Casa ${numCasaFinal}: ${msg}`, fila: numFilaExcel });
            }

            const eqList = getTagEquivalents(val);
            eqList.forEach((eq) => {
              if (tagsVistosEnFila.has(eq)) {
                const prevSlot = tagsVistosEnFila.get(eq);
                const msg = `El tag "${val}" está repetido internamente en ${prevSlot} y ${getTagLabel(tk)}.`;
                conflictosFila.push(msg);
                conflictos.push({ tipo: 'DUPLICADO_INTERNO', mensaje: `Casa ${numCasaFinal}: ${msg}`, fila: numFilaExcel });
              } else {
                tagsVistosEnFila.set(eq, getTagLabel(tk));
              }

              // 2. Registrar en globalTagsMap para auditoría entre viviendas del archivo
              if (!globalTagsMap.has(eq)) {
                globalTagsMap.set(eq, []);
              }
              globalTagsMap.get(eq).push({
                casaId,
                numeroCasa: numCasaFinal,
                tagKey: tk,
                tagLabel: getTagLabel(tk),
                tagValor: val,
                filaExcel: numFilaExcel
              });
            });
          }
        });

        // Comparar si hay cambios respecto a los tags actuales
        let tieneCambios = false;
        TAG_KEYS.forEach((tk) => {
          const anterior = tagsActuales[tk] || '';
          const nuevo = tagsExtraidos[tk] || '';
          if (anterior !== nuevo) {
            tieneCambios = true;
          }
        });

        filasAuditadas.push({
          filaExcel: numFilaExcel,
          casaId,
          numeroCasa: numCasaFinal,
          nombreResidente: nombreFinal,
          tagsNuevos: tagsExtraidos,
          tagsActuales,
          tieneCambios,
          conflictosFila,
          estado: conflictosFila.length > 0 ? 'conflicto' : (tieneCambios ? 'cambio' : 'sin_cambio')
        });
      });

      // 3. Detectar colisiones entre distintas viviendas del archivo
      globalTagsMap.forEach((asignaciones, eqTag) => {
        // Filtrar casas distintas
        const casasDistintas = [];
        const seenCasas = new Set();
        asignaciones.forEach((asig) => {
          if (!seenCasas.has(asig.casaId)) {
            seenCasas.add(asig.casaId);
            casasDistintas.push(asig);
          }
        });

        if (casasDistintas.length > 1) {
          const casasTexto = casasDistintas.map(c => `Casa ${c.numeroCasa} (${c.tagLabel})`).join(' y ');
          const tagEjemplo = asignaciones[0].tagValor;
          const msg = `El Tag "${tagEjemplo}" está asignado a múltiples viviendas en el archivo: ${casasTexto}.`;

          conflictos.push({
            tipo: 'DUPLICADO_GLOBAL',
            mensaje: msg,
            tag: tagEjemplo,
            casas: casasDistintas
          });

          // Marcar las filas afectadas en la tabla
          casasDistintas.forEach((cd) => {
            const f = filasAuditadas.find(row => row.casaId === cd.casaId);
            if (f) {
              f.conflictosFila.push(`Colisión de Tag con otra vivienda: "${tagEjemplo}"`);
              f.estado = 'conflicto';
            }
          });
        }
      });

      // 4. Detectar colisiones contra viviendas existentes en la BD que NO están en el archivo
      const casasEnArchivoIds = new Set(filasAuditadas.map(f => f.casaId));
      casasLocales.forEach((casaBd) => {
        if (casasEnArchivoIds.has(casaBd.id)) return; // Ya auditada en el archivo

        const numCasaBd = String(casaBd.numero || casaBd.numeroCasa || casaBd.id).trim();
        TAG_KEYS.forEach((tk) => {
          const valBd = cleanTagValue(casaBd[tk]);
          if (valBd) {
            const eqList = getTagEquivalents(valBd);
            eqList.forEach((eq) => {
              if (globalTagsMap.has(eq)) {
                const asignacionEnArchivo = globalTagsMap.get(eq)[0];
                const msg = `El Tag "${valBd}" en Casa ${asignacionEnArchivo.numeroCasa} colisiona con la vivienda ${numCasaBd} registrada en el sistema.`;
                conflictos.push({
                  tipo: 'COLISION_BD',
                  mensaje: msg,
                  tag: valBd
                });
                const f = filasAuditadas.find(row => row.casaId === asignacionEnArchivo.casaId);
                if (f) {
                  f.conflictosFila.push(`El tag "${valBd}" ya pertenece a la vivienda ${numCasaBd} en la base de datos.`);
                  f.estado = 'conflicto';
                }
              }
            });
          }
        });
      });

      // Contadores
      let totalTagsValidos = 0;
      filasAuditadas.forEach((f) => {
        TAG_KEYS.forEach((tk) => {
          if (f.tagsNuevos[tk]) totalTagsValidos++;
        });
      });

      const casasConCambios = filasAuditadas.filter(f => f.tieneCambios && f.conflictosFila.length === 0).length;

      setResultadoAuditoria({
        totalFilas: rows.length,
        totalTagsValidos,
        casasConCambios,
        conflictos,
        filas: filasAuditadas
      });
    } catch (err) {
      console.error('[ModalImportarTagsRFID] Error leyendo archivo Excel:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error al leer el archivo',
        text: 'No se pudo interpretar el archivo Excel. Verifica que tenga un formato válido (.xlsx, .xls o .csv).'
      });
    } finally {
      setProcesandoArchivo(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) procesarArchivoExcel(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) procesarArchivoExcel(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // 3A. Mostrar confirmación integrada en el modal
  const handleSolicitarConfirmacion = () => {
    if (!resultadoAuditoria || resultadoAuditoria.conflictos.length > 0) return;

    const filasParaActualizar = resultadoAuditoria.filas.filter(
      (f) => f.tieneCambios && f.conflictosFila.length === 0
    );

    if (filasParaActualizar.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin modificaciones',
        text: 'Los tags en el archivo coinciden exactamente con los registros actuales del fraccionamiento. No hay cambios pendientes.'
      });
      return;
    }

    setMostrarConfirmacion(true);
  };

  // 3B. Ejecutar guardado por lotes con barra de progreso en tiempo real
  const ejecutarGuardadoEnFirestore = async () => {
    if (!resultadoAuditoria || resultadoAuditoria.conflictos.length > 0) return;

    const filasParaActualizar = resultadoAuditoria.filas.filter(
      (f) => f.tieneCambios && f.conflictosFila.length === 0
    );

    if (filasParaActualizar.length === 0) return;

    setMostrarConfirmacion(false);
    setAplicandoCambios(true);

    // Lotes de 20 viviendas para retroalimentación visual fluida en tiempo real
    const CHUNK_SIZE = 20;
    const chunks = [];
    for (let i = 0; i < filasParaActualizar.length; i += CHUNK_SIZE) {
      chunks.push(filasParaActualizar.slice(i, i + CHUNK_SIZE));
    }

    const totalViviendas = filasParaActualizar.length;
    const totalLotes = chunks.length;

    setProgresoCarga({
      procesadas: 0,
      total: totalViviendas,
      porcentaje: 0,
      loteActual: 1,
      totalLotes,
      rangoActual: 'Iniciando sincronización con Firebase...'
    });

    try {
      const timestampIso = new Date().toISOString();
      const editorEmail = userData?.email || 'admin';
      let acumuladorProcesadas = 0;

      for (let idx = 0; idx < chunks.length; idx++) {
        const chunk = chunks[idx];
        const primeraCasa = chunk[0]?.numeroCasa || '';
        const ultimaCasa = chunk[chunk.length - 1]?.numeroCasa || '';

        setProgresoCarga({
          procesadas: acumuladorProcesadas,
          total: totalViviendas,
          porcentaje: Math.round((acumuladorProcesadas / totalViviendas) * 100),
          loteActual: idx + 1,
          totalLotes,
          rangoActual: `Sincronizando ${primeraCasa} a ${ultimaCasa}...`
        });

        const batch = writeBatch(db);

        chunk.forEach((item) => {
          const casaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', item.casaId);
          const updatePayload = {
            zk_sync_status: 'pendiente',
            ultimaActualizacionTags: timestampIso,
            modificadoPor: editorEmail,
          };

          TAG_KEYS.forEach((tk) => {
            const nuevoVal = item.tagsNuevos[tk];
            if (nuevoVal) {
              updatePayload[tk] = nuevoVal;
            } else {
              updatePayload[tk] = deleteField();
            }
          });

          batch.update(casaRef, updatePayload);
        });

        await batch.commit();

        acumuladorProcesadas += chunk.length;
        const pct = Math.min(100, Math.round((acumuladorProcesadas / totalViviendas) * 100));

        setProgresoCarga({
          procesadas: acumuladorProcesadas,
          total: totalViviendas,
          porcentaje: pct,
          loteActual: idx + 1,
          totalLotes,
          rangoActual: `Completado: ${primeraCasa} a ${ultimaCasa}`
        });

        // Pausa breve para permitir el repintado suave de la barra de progreso en el navegador
        await new Promise((r) => setTimeout(r, 150));
      }

      // Registro en la bitácora de movimientos con firma oficial de registerMovement
      try {
        await registerMovement({
          fraccionamientoId,
          titulo: 'Importación masiva de tarjetas RFID',
          descripcion: `Actualización masiva de tarjetas RFID en ${totalViviendas} viviendas (${resultadoAuditoria.totalTagsValidos} tags leídos).`,
          categoria: 'RFID',
          tipoMovimiento: 'importacion_masiva_tags_rfid',
          metadata: {
            viviendasActualizadas: totalViviendas,
            tagsTotales: resultadoAuditoria.totalTagsValidos,
            fecha: timestampIso
          }
        });
      } catch (logErr) {
        console.warn('[ModalImportarTagsRFID] No se pudo registrar movimiento en bitácora:', logErr);
      }

      setImportacionCompletada({
        viviendasActualizadas: totalViviendas,
        tagsTotales: resultadoAuditoria.totalTagsValidos
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('[ModalImportarTagsRFID] Error al guardar en Firestore:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error al aplicar cambios',
        text: 'Ocurrió un error al guardar los tags en la base de datos: ' + (err.message || 'Error desconocido')
      });
    } finally {
      setAplicandoCambios(false);
    }
  };

  // Filtrado de la tabla de vista previa
  const filasFiltradas = useMemo(() => {
    if (!resultadoAuditoria?.filas) return [];
    let list = resultadoAuditoria.filas;

    if (filtroTabla === 'cambios') {
      list = list.filter(f => f.tieneCambios);
    } else if (filtroTabla === 'conflictos') {
      list = list.filter(f => f.conflictosFila.length > 0);
    }

    if (busquedaTabla.trim()) {
      const q = busquedaTabla.trim().toLowerCase();
      list = list.filter(f =>
        f.numeroCasa.toLowerCase().includes(q) ||
        f.nombreResidente.toLowerCase().includes(q) ||
        Object.values(f.tagsNuevos).some(v => String(v).toLowerCase().includes(q))
      );
    }

    return list;
  }, [resultadoAuditoria, filtroTabla, busquedaTabla]);

  if (!isOpen) return null;

  const tieneConflictos = resultadoAuditoria && resultadoAuditoria.conflictos.length > 0;
  const casasConCambiosCount = resultadoAuditoria?.casasConCambios || 0;

  return (
    <div className="modal-rfid-overlay" role="dialog" aria-modal="true">
      <div className="modal-rfid-container">
        {/* Encabezado */}
        <div className="modal-rfid-header">
          <div className="modal-rfid-header-title">
            <div className="modal-rfid-header-icon">
              <FaIdCard />
            </div>
            <div>
              <h2>Importación Masiva de Tarjetas RFID Tag's</h2>
              <p>Actualiza y blinda la asignación de tags en todo el fraccionamiento mediante plantilla Excel</p>
            </div>
          </div>
          <button
            type="button"
            className="modal-rfid-close-btn"
            onClick={handleClose}
            aria-label="Cerrar modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Cuerpo del Modal */}
        <div className="modal-rfid-body">
          {/* Alerta de diagnóstico previo de la BD si ya hay tags duplicados existentes (ej. SF01 y SF28) */}
          {auditoriaBdActual.duplicateConflicts.length > 0 && (
            <div className="rfid-alert warning">
              <FaExclamationTriangle className="rfid-alert-icon" />
              <div style={{ flex: 1 }}>
                <b>⚠️ Detección en Base de Datos:</b> Se encontraron{' '}
                <b>{auditoriaBdActual.duplicateConflicts.length} tags repetidos</b> actualmente entre diferentes viviendas del fraccionamiento.
                <div style={{ marginTop: 6 }}>
                  <button
                    type="button"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#b45309',
                      textDecoration: 'underline',
                      fontWeight: 600,
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: '0.85rem'
                    }}
                    onClick={() => setMostrarDuplicadosBd(!mostrarDuplicadosBd)}
                  >
                    {mostrarDuplicadosBd ? 'Ocultar detalle de duplicados actuales' : 'Ver detalle de duplicados actuales'}
                  </button>
                </div>

                {mostrarDuplicadosBd && (
                  <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', borderRadius: 6, fontSize: '0.8rem' }}>
                    {auditoriaBdActual.duplicateConflicts.map((dc, i) => (
                      <div key={i} style={{ marginBottom: 4 }}>
                        • Tag <code>{dc.tagOriginal}</code> compartido por:{' '}
                        {dc.casas.map(c => `Casa ${c.numeroCasa} (${c.tagLabel})`).join(', ')}
                      </div>
                    ))}
                    <div style={{ marginTop: 4, fontStyle: 'italic', color: '#78350f' }}>
                      Tip: Descarga la plantilla a continuación, corrige los tags en conflicto para que sean únicos y vuelve a subirla para sanear la base de datos.
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Paso 1: Descargar Plantilla Oficial */}
          <div className="rfid-step-card">
            <div className="rfid-step-title">
              <span className="rfid-step-badge">1</span>
              <span>Descargar plantilla con datos actuales</span>
            </div>
            <p className="rfid-step-desc">
              Obtén el archivo Excel con todas las viviendas del fraccionamiento pre-cargadas y sus tags vigentes (Tag 1 al 9).
              Modifica, agrega o elimina los números de tag necesarios manteniendo la columna <b>ID_Sistema</b> intacta.
            </p>
            <div className="rfid-step-actions">
              <button
                type="button"
                className="rfid-btn-download"
                onClick={handleDownloadPlantilla}
                disabled={cargandoCasas || casasLocales.length === 0}
              >
                <FaDownload />
                <span>Descargar plantilla oficial (.xlsx)</span>
              </button>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {casasLocales.length} viviendas listas para exportar
              </span>
            </div>
          </div>

          {/* Paso 2: Subir archivo */}
          <div className="rfid-step-card">
            <div className="rfid-step-title">
              <span className="rfid-step-badge">2</span>
              <span>Cargar archivo con tarjetas RFID</span>
            </div>
            <p className="rfid-step-desc">
              Selecciona o arrastra el archivo Excel (.xlsx, .xls, .csv). El sistema validará en tiempo real que ningún tag esté duplicado entre viviendas ni con registros existentes.
            </p>

            <div
              className={`rfid-dropzone ${isDragging ? 'dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <FaUpload className="rfid-dropzone-icon" />
              <p className="rfid-dropzone-title">
                {archivoSeleccionado
                  ? `Archivo cargado: ${archivoSeleccionado.name}`
                  : 'Haz clic aquí o arrastra tu archivo Excel'}
              </p>
              <p className="rfid-dropzone-hint">Formatos compatibles: .xlsx, .xls, .csv</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          </div>

          {/* Paso 3: Resultados de la auditoría */}
          {procesandoArchivo && (
            <div className="rfid-alert info">
              <div className="rfid-spinner" style={{ borderColor: '#3b82f6', borderTopColor: 'transparent' }} />
              <span>Analizando y blindando tarjetas del archivo...</span>
            </div>
          )}

          {resultadoAuditoria && !procesandoArchivo && (
            <div className="rfid-step-card">
              <div className="rfid-step-title">
                <span className="rfid-step-badge">3</span>
                <span>
                  {importacionCompletada
                    ? 'Resultado de la importación masiva'
                    : aplicandoCambios
                    ? 'Sincronizando tarjetas en Firebase...'
                    : mostrarConfirmacion
                    ? 'Confirmar actualización de tarjetas RFID'
                    : 'Diagnóstico y vista previa de importación'}
                </span>
              </div>

              {/* A. PANTALLA DE ÉXITO FINAL */}
              {importacionCompletada ? (
                <div className="rfid-success-screen">
                  <div className="rfid-success-icon">
                    <FaCheckCircle />
                  </div>
                  <h3 className="rfid-success-title">
                    ¡Importación Masiva Completada con Éxito!
                  </h3>
                  <p className="rfid-confirm-desc">
                    Todas las tarjetas RFID se han guardado correctamente en Firebase y se marcó el estado de sincronización pendiente para el control de acceso.
                  </p>
                  <div className="rfid-success-summary">
                    <div className="rfid-success-stat">
                      <span className="rfid-success-stat-val">
                        {importacionCompletada.viviendasActualizadas}
                      </span>
                      <span className="rfid-success-stat-lbl">
                        Viviendas Actualizadas
                      </span>
                    </div>
                    <div className="rfid-success-stat">
                      <span className="rfid-success-stat-val">
                        {importacionCompletada.tagsTotales}
                      </span>
                      <span className="rfid-success-stat-lbl">
                        Tags Vinculados
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="rfid-btn-apply"
                    style={{ backgroundColor: '#16a34a', marginTop: 8 }}
                    onClick={handleClose}
                  >
                    <FaCheckCircle />
                    <span>Finalizar y cerrar</span>
                  </button>
                </div>
              ) : aplicandoCambios && progresoCarga ? (
                /* B. BARRA DE PROGRESO EN TIEMPO REAL */
                <div className="rfid-progress-container">
                  <div className="rfid-progress-header">
                    <span>
                      Guardando tarjetas en Firebase ({progresoCarga.procesadas} de {progresoCarga.total} viviendas)
                    </span>
                    <span className="rfid-progress-percentage">
                      {progresoCarga.porcentaje}%
                    </span>
                  </div>
                  <div className="rfid-progress-bar-track">
                    <div
                      className="rfid-progress-bar-fill"
                      style={{ width: `${progresoCarga.porcentaje}%` }}
                    />
                  </div>
                  <div className="rfid-progress-status-text">
                    Procesando Lote {progresoCarga.loteActual} de {progresoCarga.totalLotes} — No cierres esta ventana
                  </div>
                  <div className="rfid-progress-subtext">
                    {progresoCarga.rangoActual}
                  </div>
                </div>
              ) : mostrarConfirmacion ? (
                /* C. VISTA DE CONFIRMACIÓN INTEGRADA */
                <div className="rfid-confirm-box">
                  <div className="rfid-confirm-icon">
                    <FaShieldAlt />
                  </div>
                  <h3 className="rfid-confirm-title">
                    ¿Confirmas la actualización masiva de tarjetas RFID?
                  </h3>
                  <p className="rfid-confirm-desc">
                    Estás a punto de actualizar las tarjetas RFID de <b>{casasConCambiosCount} viviendas</b> ({resultadoAuditoria.totalTagsValidos} tags activos verificados con <b>0 conflictos</b>). El sistema actualizará la base de datos y registrará la operación en la bitácora del fraccionamiento.
                  </p>
                  <div className="rfid-confirm-actions">
                    <button
                      type="button"
                      className="rfid-btn-cancel"
                      onClick={() => setMostrarConfirmacion(false)}
                    >
                      Volver a la vista previa
                    </button>
                    <button
                      type="button"
                      className="rfid-btn-apply"
                      onClick={ejecutarGuardadoEnFirestore}
                    >
                      <FaCheckCircle />
                      <span>Sí, iniciar carga a Firebase ahora</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* D. VISTA PREVIA ESTÁNDAR */
                <>
                  {/* Métricas */}
                  <div className="rfid-metrics-grid">
                    <div className="rfid-metric-card">
                      <span className="rfid-metric-label">Viviendas en archivo</span>
                      <span className="rfid-metric-value">{resultadoAuditoria.totalFilas}</span>
                    </div>
                    <div className="rfid-metric-card">
                      <span className="rfid-metric-label">Tags válidos leídos</span>
                      <span className="rfid-metric-value">{resultadoAuditoria.totalTagsValidos}</span>
                    </div>
                    <div className={`rfid-metric-card ${casasConCambiosCount > 0 ? 'success' : ''}`}>
                      <span className="rfid-metric-label">Viviendas con cambios</span>
                      <span className="rfid-metric-value">{casasConCambiosCount}</span>
                    </div>
                    <div className={`rfid-metric-card ${tieneConflictos ? 'danger' : 'success'}`}>
                      <span className="rfid-metric-label">Conflictos detectados</span>
                      <span className="rfid-metric-value">{resultadoAuditoria.conflictos.length}</span>
                    </div>
                  </div>

                  {/* Si hay conflictos */}
                  {tieneConflictos ? (
                    <div className="rfid-conflicts-list">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: '#991b1b', fontSize: '0.9rem' }}>
                        <FaShieldAlt /> Blindaje Activo: Se detectaron {resultadoAuditoria.conflictos.length} colisiones de tarjetas
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#7f1d1d' }}>
                        Para proteger el control de acceso, debes resolver estas colisiones en el archivo antes de poder aplicar los cambios:
                      </div>
                      {resultadoAuditoria.conflictos.map((conf, idx) => (
                        <div key={idx} className="rfid-conflict-item">
                          <FaExclamationCircle style={{ flexShrink: 0, marginTop: 2 }} />
                          <span>{conf.mensaje}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rfid-alert success">
                      <FaCheckCircle className="rfid-alert-icon" />
                      <div>
                        <b>Blindaje Aprobado:</b> Todos los tags son únicos y no generan colisiones con otras viviendas.
                        {casasConCambiosCount > 0
                          ? ` Listo para actualizar ${casasConCambiosCount} viviendas.`
                          : ' No se detectaron diferencias con respecto a los datos vigentes.'}
                      </div>
                    </div>
                  )}

                  {/* Filtros de la tabla */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        type="button"
                        className={`status-button ${filtroTabla === 'todos' ? 'active' : ''}`}
                        style={{
                          padding: '5px 12px',
                          fontSize: '0.8rem',
                          borderRadius: 6,
                          background: filtroTabla === 'todos' ? '#4f46e5' : '#f1f5f9',
                          color: filtroTabla === 'todos' ? '#fff' : '#475569',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFiltroTabla('todos')}
                      >
                        Todos ({resultadoAuditoria.filas.length})
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '5px 12px',
                          fontSize: '0.8rem',
                          borderRadius: 6,
                          background: filtroTabla === 'cambios' ? '#16a34a' : '#f1f5f9',
                          color: filtroTabla === 'cambios' ? '#fff' : '#475569',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFiltroTabla('cambios')}
                      >
                        Con cambios ({casasConCambiosCount})
                      </button>
                      {tieneConflictos && (
                        <button
                          type="button"
                          style={{
                            padding: '5px 12px',
                            fontSize: '0.8rem',
                            borderRadius: 6,
                            background: filtroTabla === 'conflictos' ? '#dc2626' : '#f1f5f9',
                            color: filtroTabla === 'conflictos' ? '#fff' : '#475569',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onClick={() => setFiltroTabla('conflictos')}
                        >
                          Con conflictos ({resultadoAuditoria.conflictos.length})
                        </button>
                      )}
                    </div>

                    <div style={{ position: 'relative', width: 220 }}>
                      <FaSearch style={{ position: 'absolute', left: 10, top: 9, color: '#94a3b8', fontSize: '0.8rem' }} />
                      <input
                        type="text"
                        placeholder="Buscar casa o tag..."
                        value={busquedaTabla}
                        onChange={(e) => setBusquedaTabla(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px 10px 6px 30px',
                          fontSize: '0.8rem',
                          border: '1px solid #cbd5e1',
                          borderRadius: 6,
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  {/* Tabla de previsualización */}
                  <div className="rfid-preview-table-wrapper">
                    <table className="rfid-preview-table">
                      <thead>
                        <tr>
                          <th style={{ width: '90px' }}>Casa</th>
                          <th style={{ width: '160px' }}>Residente</th>
                          <th>Tags a Asignar</th>
                          <th style={{ width: '120px' }}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filasFiltradas.length === 0 ? (
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
                              No hay registros que coincidan con el filtro seleccionado.
                            </td>
                          </tr>
                        ) : (
                          filasFiltradas.map((item, idx) => {
                            const tagsList = Object.entries(item.tagsNuevos).filter(([_, v]) => Boolean(v));
                            return (
                              <tr
                                key={idx}
                                className={
                                  item.estado === 'conflicto'
                                    ? 'has-conflict'
                                    : item.estado === 'cambio'
                                    ? 'has-change'
                                    : ''
                                }
                              >
                                <td>
                                  <b>{item.numeroCasa}</b>
                                </td>
                                <td>{item.nombreResidente || '—'}</td>
                                <td>
                                  {tagsList.length === 0 ? (
                                    <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                      Sin tags asignados
                                    </span>
                                  ) : (
                                    tagsList.map(([slot, tagVal], tIdx) => {
                                      const tagAnterior = item.tagsActuales[slot];
                                      const esNuevo = tagAnterior !== tagVal;
                                      return (
                                        <span
                                          key={tIdx}
                                          className={`rfid-tag-chip ${esNuevo ? 'new' : ''}`}
                                          title={`${getTagLabel(slot)}: ${tagVal}${esNuevo ? ' (Modificado)' : ''}`}
                                        >
                                          {tagVal}
                                        </span>
                                      );
                                    })
                                  )}
                                  {item.conflictosFila.length > 0 && (
                                    <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: 4, fontWeight: 600 }}>
                                      ⚠️ {item.conflictosFila.join(' | ')}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  {item.estado === 'conflicto' ? (
                                    <span className="rfid-status-badge conflict">
                                      <FaExclamationCircle /> Conflicto
                                    </span>
                                  ) : item.estado === 'cambio' ? (
                                    <span className="rfid-status-badge changed">
                                      <FaCheckCircle /> Modificado
                                    </span>
                                  ) : (
                                    <span className="rfid-status-badge unchanged">Sin cambios</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!importacionCompletada && (
          <div className="modal-rfid-footer">
            <button
              type="button"
              className="rfid-btn-cancel"
              onClick={handleClose}
              disabled={aplicandoCambios}
            >
              Cancelar
            </button>
            {!mostrarConfirmacion && (
              <button
                type="button"
                className="rfid-btn-apply"
                onClick={handleSolicitarConfirmacion}
                disabled={
                  !resultadoAuditoria ||
                  tieneConflictos ||
                  casasConCambiosCount === 0 ||
                  aplicandoCambios
                }
              >
                {aplicandoCambios ? (
                  <>
                    <span className="rfid-spinner" />
                    <span>Sincronizando con Firebase...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle />
                    <span>
                      {casasConCambiosCount > 0
                        ? `Aplicar cambios a ${casasConCambiosCount} vivienda${casasConCambiosCount === 1 ? '' : 's'}`
                        : 'Aplicar cambios'}
                    </span>
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
